import type { UseMutationResult } from '@tanstack/react-query';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import type { JobFamily } from '~/shared/data-contract/job-family';
import type { LanguageCodeForJSA } from '~/shared/internationalization';

import { useApplicationFetchers } from '../../fetchers/use-application-fetchers';
import { jobArchitectureKeys } from './query-keys';
import { updateFamiliesWithClustersCache } from './use-families-with-clusters';

export type UsePatchJobFamilyParams = {
    readonly jobFamily: JobFamily;
    readonly languageCode: LanguageCodeForJSA;
    readonly withAutoTranslation: boolean;
};

export type UsePatchJobFamilyResult = UseMutationResult<JobFamily, Error, UsePatchJobFamilyParams>;

export const usePatchJobFamily = (): UsePatchJobFamilyResult => {
    const { patchJobFamily } = useApplicationFetchers();

    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({ jobFamily, languageCode, withAutoTranslation }: UsePatchJobFamilyParams) => {
            const result = await patchJobFamily({
                jobFamily,
                languageCode,
                withAutoTranslation,
            });
            if (result.isErr) {
                throw result.error;
            }

            return result.value;
        },
        onMutate: (params) => {
            const { architectureId } = params.jobFamily;
            queryClient.cancelQueries({
                queryKey: jobArchitectureKeys.familiesWithClusters(architectureId),
            });

            updateFamiliesWithClustersCache(queryClient, architectureId, (old) =>
                old.map((family) =>
                    family.id === params.jobFamily.id
                        ? { ...family, translations: params.jobFamily.translations }
                        : family,
                ),
            );
        },
        onSuccess: (_, { jobFamily }) => {
            queryClient.invalidateQueries({
                queryKey: jobArchitectureKeys.familiesWithClusters(jobFamily.architectureId),
            });
        },
        throwOnError: false,
    });
};
